/**
 * SalaryHistoryPanel — Historique mensuel de la masse salariale (12 mois).
 *
 * Affiche :
 *  - Sélecteur année
 *  - Résumé cumul annuel (brut · net · CNSS · ITS)
 *  - Tableau 12 mois avec brut / net / cotisations / employés / bulletins signés
 *  - Mini-bar-chart visuel du brut/net par mois
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, TrendingUp, Users, ShieldCheck, Loader2, RefreshCw, FileText, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => (Math.round(v || 0)).toLocaleString("fr-FR");
const MONTHS_SHORT = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
const MONTHS_FULL = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

const formatFrShort = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  } catch { return iso; }
};

const SalaryHistoryPanel = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState({ year, months: [], totals: { brut: 0, net: 0, cnss_emp: 0, its: 0 } });
  const [loading, setLoading] = useState(false);
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/salaries/history`, { params: { year } });
      setData(res.data);
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  // Max value for bar chart scaling
  const maxBrut = useMemo(() => {
    const vals = (data.months || []).map((m) => m.total_brut || 0);
    return Math.max(1, ...vals);
  }, [data.months]);

  const kpis = [
    { label: "Cumul BRUT annuel", value: fmt(data.totals?.brut), suffix: "F", accent: "#3b82f6" },
    { label: "Cumul NET annuel", value: fmt(data.totals?.net), suffix: "F", accent: "#10b981" },
    { label: "CNSS versée", value: fmt(data.totals?.cnss_emp), suffix: "F", accent: "#a855f7" },
    { label: "ITS retenue", value: fmt(data.totals?.its), suffix: "F", accent: "#f59e0b" },
  ];

  const exportCsv = () => {
    const lines = ["Mois;Brut;Net;Payable_le;CNSS;ITS;Primes;Retenues;Employes;Payes"];
    (data.months || []).forEach((m) => {
      lines.push([
        `${MONTHS_FULL[m.month - 1]} ${m.year}`,
        Math.round(m.total_brut), Math.round(m.total_net),
        m.payment_planned_date || "",
        Math.round(m.total_cnss_emp), Math.round(m.total_its),
        Math.round(m.total_bonuses), Math.round(m.total_deductions),
        m.nb_employes, m.nb_payslips_payes || 0,
      ].join(";"));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `historique_salaires_${year}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success("CSV téléchargé");
  };

  const exportPdf = async () => {
    if (!data?.months?.length) {
      toast.error("Aucune donnée à exporter");
      return;
    }
    // Fetch company info for header (best-effort)
    let co = {};
    try {
      const cr = await axios.get(`${API}/salaries/company`);
      co = cr.data || {};
    } catch { /* ignore */ }

    const w = window.open("", "_blank", "width=1000,height=800");
    if (!w) {
      toast.error("Popup bloquée — autorisez les pop-ups pour imprimer");
      return;
    }
    const generatedAt = new Date().toLocaleDateString("fr-FR", {
      day: "numeric", month: "long", year: "numeric",
    });
    const generatedTime = new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

    const formatDate = (iso) => {
      if (!iso) return "—";
      try {
        return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "2-digit" });
      } catch { return iso; }
    };

    // Group rows
    const monthRows = (data.months || []).map((m) => {
      const isPast = (m.year < currentYear) || (m.year === currentYear && m.month <= currentMonth);
      const bg = isPast ? "#ffffff" : "#f8fafc";
      const fade = isPast ? "" : "opacity:0.6;";
      const paidPct = m.nb_employes > 0 ? Math.round((m.nb_payslips_payes || 0) / m.nb_employes * 100) : 0;
      const paidColor = paidPct >= 100 ? "#065f46" : paidPct >= 50 ? "#a16207" : "#7f1d1d";
      return `
        <tr style="background:${bg};${fade}">
          <td style="padding:6px 8px;font-weight:700;color:#0f172a;font-size:11px">
            ${MONTHS_FULL[m.month - 1]} ${m.year}
            ${(m.year === currentYear && m.month === currentMonth) ? '<span style="margin-left:4px;padding:1px 5px;background:#dbeafe;color:#1e40af;border-radius:8px;font-size:8.5px;font-weight:800;letter-spacing:0.05em;text-transform:uppercase">Actuel</span>' : ''}
          </td>
          <td style="padding:6px 8px;text-align:right;font-family:'Courier New',monospace;font-weight:800;color:#1e40af;font-size:11px">${fmt(m.total_brut)}</td>
          <td style="padding:6px 8px;text-align:right;font-family:'Courier New',monospace;font-weight:900;color:#065f46;font-size:11.5px">${fmt(m.total_net)}</td>
          <td style="padding:6px 8px;font-family:'Courier New',monospace;color:#7c2d12;font-weight:700;font-size:10.5px;text-align:center">${formatDate(m.payment_planned_date)}</td>
          <td style="padding:6px 8px;text-align:right;font-family:'Courier New',monospace;color:#6b21a8;font-weight:700;font-size:10.5px">${fmt(m.total_cnss_emp)}</td>
          <td style="padding:6px 8px;text-align:right;font-family:'Courier New',monospace;color:#a16207;font-weight:700;font-size:10.5px">${fmt(m.total_its)}</td>
          <td style="padding:6px 8px;text-align:right;font-family:'Courier New',monospace;color:#334155;font-weight:600;font-size:10.5px">${fmt(m.total_bonuses)}</td>
          <td style="padding:6px 8px;text-align:right;font-family:'Courier New',monospace;color:#334155;font-weight:600;font-size:10.5px">${fmt(m.total_deductions)}</td>
          <td style="padding:6px 8px;text-align:center;font-family:'Courier New',monospace;color:#0f172a;font-weight:700;font-size:10.5px">${m.nb_employes}</td>
          <td style="padding:6px 8px;text-align:center;font-family:'Courier New',monospace;color:${paidColor};font-weight:800;font-size:10.5px">${m.nb_payslips_payes || 0}/${m.nb_employes}</td>
        </tr>`;
    }).join("");

    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Récapitulatif salaires ${year} — ${co.name || 'Espace Maxo'}</title>
<style>
  @media print { @page { size: A4 landscape; margin: 8mm 10mm; } }
  * { box-sizing: border-box; }
  body {
    font-family: 'Helvetica Neue', 'Segoe UI', Arial, sans-serif;
    color: #0f172a; padding: 6px 12px; margin: auto;
    font-size: 11px; line-height: 1.4; font-weight: 500;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
    -webkit-font-smoothing: antialiased;
  }
  .header { display: flex; align-items: center; justify-content: space-between; padding-bottom: 8px; margin-bottom: 12px; border-bottom: 2.5px solid #7f1d1d; }
  .brand-left { display: flex; align-items: center; gap: 12px; }
  .brand-left img { height: 46px; width: auto; }
  .co-name { font-size: 14px; font-weight: 900; letter-spacing: 0.02em; color: #0F1F3D; margin: 0; }
  .co-meta { font-size: 10px; color: #1e293b; line-height: 1.4; margin-top: 2px; font-weight: 500; }
  .doc-title { text-align: right; }
  .doc-title .kicker { font-size: 9.5px; letter-spacing: 0.15em; text-transform: uppercase; color: #7f1d1d; font-weight: 800; }
  .doc-title .title { font-size: 20px; font-weight: 900; letter-spacing: 0.02em; color: #0F1F3D; margin-top: 2px; }
  .doc-title .sub { font-size: 10.5px; color: #334155; margin-top: 3px; font-weight: 600; }
  .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 12px; }
  .kpi { padding: 9px 12px; border-radius: 6px; border: 1.5px solid #cbd5e1; background: #f8fafc; }
  .kpi .lbl { font-size: 9px; letter-spacing: 0.14em; text-transform: uppercase; color: #334155; font-weight: 800; }
  .kpi .val { font-family: 'Courier New', monospace; font-size: 17px; font-weight: 900; letter-spacing: 0.02em; margin-top: 3px; }
  .kpi.brut { border-left: 4px solid #3b82f6; } .kpi.brut .val { color: #1e40af; }
  .kpi.net  { border-left: 4px solid #10b981; } .kpi.net .val  { color: #065f46; }
  .kpi.cnss { border-left: 4px solid #a855f7; } .kpi.cnss .val { color: #6b21a8; }
  .kpi.its  { border-left: 4px solid #f59e0b; } .kpi.its .val  { color: #a16207; }
  h2 { font-size: 11px; text-transform: uppercase; letter-spacing: 0.14em; color: #7f1d1d; margin: 12px 0 6px; font-weight: 900; }
  table { width: 100%; border-collapse: collapse; border: 1.5px solid #64748b; border-radius: 6px; overflow: hidden; }
  table thead tr { background: #0F1F3D; color: white; font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 800; }
  table thead td { padding: 6px 8px; border-right: 1px solid rgba(255,255,255,0.15); }
  table tbody tr { border-top: 1px solid #cbd5e1; }
  table tbody tr:nth-child(even) { background: #f1f5f9; }
  table tfoot tr { background: #0F1F3D; color: white; font-weight: 900; font-size: 11.5px; border-top: 2.5px solid #7f1d1d; }
  table tfoot td { padding: 8px 8px; }
  .legend { margin-top: 8px; padding: 8px 12px; background: #f1f5f9; border-left: 3px solid #64748b; border-radius: 4px; font-size: 10px; color: #0f172a; line-height: 1.5; font-weight: 500; }
  .legend b { color: #0F1F3D; }
  .footer { margin-top: 14px; padding-top: 8px; border-top: 1px dotted #94a3b8; display: flex; justify-content: space-between; font-size: 9px; color: #334155; font-weight: 500; }
</style></head><body>

  <div class="header">
    <div class="brand-left">
      ${co.logo_url ? `<img src="${co.logo_url}" alt="${co.name || 'Espace Maxo'}" onerror="this.style.display='none'" />` : ''}
      <div>
        <p class="co-name">${co.name || 'ESPACE MAXO SARL'}</p>
        <div class="co-meta">
          ${co.tagline || 'Restaurant &amp; Jeux VR'}<br/>
          ${co.address || ''}<br/>
          IFU : ${co.ifu || '—'} · RCCM : ${co.rccm || '—'}${co.phone ? ' · Tél : ' + co.phone : ''}
        </div>
      </div>
    </div>
    <div class="doc-title">
      <div class="kicker">Récapitulatif de la masse salariale</div>
      <div class="title">Exercice ${year}</div>
      <div class="sub">Généré le ${generatedAt} à ${generatedTime}</div>
    </div>
  </div>

  <!-- KPI CUMUL ANNUEL -->
  <div class="kpi-grid">
    <div class="kpi brut">
      <div class="lbl">Cumul brut annuel</div>
      <div class="val">${fmt(data.totals?.brut)} F</div>
    </div>
    <div class="kpi net">
      <div class="lbl">Cumul net annuel</div>
      <div class="val">${fmt(data.totals?.net)} F</div>
    </div>
    <div class="kpi cnss">
      <div class="lbl">CNSS versée (part salariale)</div>
      <div class="val">${fmt(data.totals?.cnss_emp)} F</div>
    </div>
    <div class="kpi its">
      <div class="lbl">ITS retenue</div>
      <div class="val">${fmt(data.totals?.its)} F</div>
    </div>
  </div>

  <h2>Détail mensuel — ${year}</h2>
  <table>
    <thead>
      <tr>
        <td style="width:16%">Mois</td>
        <td style="text-align:right;width:10%">Brut versé</td>
        <td style="text-align:right;width:10%">Net à payer</td>
        <td style="text-align:center;width:10%">Payable le</td>
        <td style="text-align:right;width:9%">CNSS emp.</td>
        <td style="text-align:right;width:9%">ITS</td>
        <td style="text-align:right;width:9%">Primes</td>
        <td style="text-align:right;width:9%">Retenues</td>
        <td style="text-align:center;width:8%">Emp.</td>
        <td style="text-align:center;width:10%">Payés</td>
      </tr>
    </thead>
    <tbody>${monthRows}</tbody>
    <tfoot>
      <tr>
        <td style="padding:8px 8px;font-weight:900;letter-spacing:0.08em;text-transform:uppercase;font-size:10.5px">Total ${year}</td>
        <td style="text-align:right;font-family:'Courier New',monospace">${fmt(data.totals?.brut)}</td>
        <td style="text-align:right;font-family:'Courier New',monospace">${fmt(data.totals?.net)}</td>
        <td></td>
        <td style="text-align:right;font-family:'Courier New',monospace">${fmt(data.totals?.cnss_emp)}</td>
        <td style="text-align:right;font-family:'Courier New',monospace">${fmt(data.totals?.its)}</td>
        <td colspan="4"></td>
      </tr>
    </tfoot>
  </table>

  <div class="legend">
    <b>Règle de paiement :</b> Les nets d'un mois M sont versés le <b>10 du mois M+1</b> (colonne « Payable le »). La colonne « Payés » indique le nombre de bulletins effectivement marqués payés / effectif du mois.<br/>
    <b>Calcul du net :</b> Brut − CNSS employé (3,6 %) − ITS progressif − avances − retenues manuelles − bons à crédit.
    <b>Charges patronales :</b> CNSS employeur (15,4 %) non affichée ici — voir bulletins individuels.
  </div>

  <div class="footer">
    <div>${co.name || 'ESPACE MAXO SARL'} — Document interne · Confidentiel</div>
    <div>Page 1/1 · Récapitulatif ${year}</div>
  </div>

  <script>window.addEventListener('load', () => { setTimeout(() => { window.print(); }, 300); });</script>
</body></html>`;
    w.document.open(); w.document.write(html); w.document.close();
    toast.success("Récapitulatif PDF ouvert");
  };

  return (
    <div className="space-y-3" data-testid="salary-history-panel">
      {/* Top bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-5 h-5 text-blue-300" strokeWidth={2.3} />
          </div>
          <div className="min-w-0">
            <p className="text-white font-bold text-[15px] leading-tight">Historique mensuel — Masse salariale</p>
            <p className="text-slate-500 text-[11px] leading-tight">Brut · Net · Cotisations OHADA · 12 mois glissants</p>
          </div>
        </div>

        <div className="flex items-center gap-1 bg-slate-800/60 border border-slate-700 rounded-lg px-1 py-0.5 flex-shrink-0">
          <Button size="sm" variant="ghost" onClick={() => setYear((y) => y - 1)} className="h-7 w-7 p-0 text-slate-300" data-testid="history-prev-year">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-[13px] font-bold text-white px-3 min-w-[60px] text-center">{year}</div>
          <Button size="sm" variant="ghost" onClick={() => setYear((y) => y + 1)} className="h-7 w-7 p-0 text-slate-300" data-testid="history-next-year">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <Button onClick={load} disabled={loading} size="sm" variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-700 h-9 flex-shrink-0">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </Button>
        <Button
          onClick={exportPdf}
          disabled={loading || !data?.months?.length}
          size="sm"
          variant="outline"
          className="border-rose-600/50 text-rose-300 hover:bg-rose-500/10 h-9 flex-shrink-0"
          data-testid="history-export-pdf"
        >
          <Printer className="w-3.5 h-3.5 mr-1" /> Export PDF
        </Button>
        <Button
          onClick={exportCsv}
          disabled={loading}
          size="sm"
          variant="outline"
          className="border-emerald-600/50 text-emerald-300 hover:bg-emerald-500/10 h-9 flex-shrink-0"
          data-testid="history-export-csv"
        >
          <FileText className="w-3.5 h-3.5 mr-1" /> Export CSV
        </Button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {kpis.map((k) => (
          <div
            key={k.label}
            className="rounded-lg bg-slate-800/60 border border-slate-700 p-2.5"
            style={{ borderLeft: `3px solid ${k.accent}` }}
          >
            <p className="text-[9.5px] font-bold text-slate-400 uppercase tracking-wider">{k.label}</p>
            <p className="text-white font-black text-[15px] font-mono mt-0.5">
              {k.value}<span className="text-[10px] text-slate-500 ml-1">{k.suffix}</span>
            </p>
          </div>
        ))}
      </div>

      {/* Tableau + mini bar chart */}
      {loading ? (
        <div className="py-10 text-center text-slate-400">Chargement…</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-700 bg-slate-800/40">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-slate-900/60 text-slate-300 text-[10px] uppercase tracking-wider">
                <th className="text-left px-3 py-2 font-bold">Mois</th>
                <th className="text-right px-2 py-2 font-bold text-blue-300">Brut versé</th>
                <th className="text-right px-2 py-2 font-bold text-emerald-300">Net à payer</th>
                <th className="text-left px-2 py-2 font-bold text-amber-300 hidden sm:table-cell">Payable le</th>
                <th className="text-right px-2 py-2 font-bold hidden md:table-cell">CNSS</th>
                <th className="text-right px-2 py-2 font-bold hidden md:table-cell">ITS</th>
                <th className="text-center px-2 py-2 font-bold hidden lg:table-cell">Employés</th>
                <th className="text-center px-2 py-2 font-bold hidden lg:table-cell">Payés</th>
                <th className="text-left px-3 py-2 font-bold w-[20%]">Comparatif</th>
              </tr>
            </thead>
            <tbody>
              {(data.months || []).map((m) => {
                const brutPct = maxBrut > 0 ? Math.round((m.total_brut / maxBrut) * 100) : 0;
                const netPct = maxBrut > 0 ? Math.round((m.total_net / maxBrut) * 100) : 0;
                const isFuture = m.year > currentYear || (m.year === currentYear && m.month > currentMonth);
                const isCurrent = m.year === currentYear && m.month === currentMonth;
                return (
                  <tr
                    key={m.period}
                    className={`border-t border-slate-700/50 ${isCurrent ? "bg-blue-500/5" : ""} ${isFuture ? "opacity-40" : ""}`}
                    data-testid={`history-row-${m.period}`}
                  >
                    <td className="px-3 py-2 font-semibold text-slate-200 whitespace-nowrap">
                      {MONTHS_FULL[m.month - 1]}
                      {isCurrent && <span className="ml-2 text-[9px] text-blue-300 bg-blue-500/20 px-1.5 py-0.5 rounded font-bold">Actuel</span>}
                    </td>
                    <td className="px-2 py-2 text-right font-mono font-bold text-blue-200">{fmt(m.total_brut)}</td>
                    <td className="px-2 py-2 text-right font-mono font-bold text-emerald-300">{fmt(m.total_net)}</td>
                    <td className="px-2 py-2 hidden sm:table-cell text-amber-200 text-[11px] font-mono whitespace-nowrap">
                      {formatFrShort(m.payment_planned_date)}
                      {typeof m.nb_payslips_payes === "number" && m.nb_payslips_payes > 0 && (
                        <span className="ml-1 text-emerald-400 text-[9.5px]" title={`${m.nb_payslips_payes} paie(s) enregistrée(s)`}>
                          ({m.nb_payslips_payes})
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-purple-300 hidden md:table-cell">{fmt(m.total_cnss_emp)}</td>
                    <td className="px-2 py-2 text-right font-mono text-amber-300 hidden md:table-cell">{fmt(m.total_its)}</td>
                    <td className="px-2 py-2 text-center hidden lg:table-cell">
                      <span className="inline-flex items-center gap-1 text-slate-300 text-[11px] font-mono">
                        <Users className="w-3 h-3 text-slate-500" /> {m.nb_employes}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-center hidden lg:table-cell">
                      <span className={`inline-flex items-center gap-1 text-[11px] font-mono ${m.nb_payslips_payes > 0 ? "text-emerald-300" : "text-slate-500"}`}>
                        <ShieldCheck className="w-3 h-3" /> {m.nb_payslips_payes || 0}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-0.5 min-w-[120px]">
                        <div className="flex items-center gap-1">
                          <span className="text-[8.5px] text-blue-400 w-6 font-mono">B</span>
                          <div className="flex-1 h-2 bg-slate-900/60 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500/70 rounded-full transition-all" style={{ width: `${brutPct}%` }} />
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-[8.5px] text-emerald-400 w-6 font-mono">N</span>
                          <div className="flex-1 h-2 bg-slate-900/60 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500/70 rounded-full transition-all" style={{ width: `${netPct}%` }} />
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-600 bg-slate-900/40 font-bold">
                <td className="px-3 py-2 text-slate-300 uppercase text-[10.5px] tracking-wider">TOTAL {year}</td>
                <td className="px-2 py-2 text-right font-mono text-blue-200 text-[13px]">{fmt(data.totals?.brut)} F</td>
                <td className="px-2 py-2 text-right font-mono text-emerald-300 text-[13px]">{fmt(data.totals?.net)} F</td>
                <td className="hidden sm:table-cell" />
                <td className="px-2 py-2 text-right font-mono text-purple-300 hidden md:table-cell">{fmt(data.totals?.cnss_emp)} F</td>
                <td className="px-2 py-2 text-right font-mono text-amber-300 hidden md:table-cell">{fmt(data.totals?.its)} F</td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Légende */}
      <p className="text-slate-500 text-[10.5px] italic">
        <b className="text-slate-400">B</b> = brut versé (salaire + primes + transport) · <b className="text-slate-400">N</b> = net à payer (après CNSS, ITS, avances, retenues manuelles).
        La colonne <b className="text-amber-300">Payable le</b> applique la règle : nets du mois M payés le <b>10 du mois M+1</b>.
      </p>
    </div>
  );
};

export default SalaryHistoryPanel;
